/* @jsx h */
import { h, Fragment } from "preact";
import { useRef, useState } from "preact/hooks";
import Checklist from "../../components/Checklist";
import { useChecklistContext } from "../../context";
import Button from "@ingka/button";
import Printer from "@ingka/ssr-icon/paths/printer";
import Loading, { LoadingBall } from "@ingka/loading";
import InlineMessage from "@ingka/inline-message";
import variables from "/src/scss/variables.module.scss";
import { useMediaQuery } from "react-responsive";
import { generatePDF, sendGADownloadPDFClick } from "../../helpers";
import { MEDIA_QUERY_FOR_DESKTOP_VIEW } from "../../constants";
import "./checklist-page.scss";
import "./printable-styles.scss";

/*
 * This component is responsible for generating the
 * checklist component and a printable pdf button.
 *
 * For an overview of how the printable pdf generator works,
 * please read documentation here:
 * https://confluence.build.ingka.ikea.com/display/USDHCXMF/Campaign+Checklist#CampaignChecklist-PDFGenerator
 *
 */

function ChecklistPage() {
    const { title, error, loaded, forcedLoggedOut } = useChecklistContext();
    const [pdfLoading, setPDFLoadingState] = useState(false);
    const pdfRef = useRef(null);
    const { prefix: stylePrefix } = variables;

    // for resizing the print button
    const isDesktop = useMediaQuery({ query: MEDIA_QUERY_FOR_DESKTOP_VIEW });

    // it's usually faster to use .split .join to remove spaces than regex in static strings
    const getFileName = () => `${title.split(" ").join("_")}.pdf`;
    const onDownloadError = () => {
        setPDFLoadingState(false);
    };

    const handleDownload = () => {
        sendGADownloadPDFClick();
        setPDFLoadingState(true);
        generatePDF(pdfRef, getFileName(), onDownloadError);
    };

    return (
        <div className="checklist-page">
            {error ? (
                <span className={`${stylePrefix}-error-message`}>
                    Uh-oh! We can&apos;t load your checklist right now
                </span>
            ) : loaded ? (
                <Fragment>
                    {forcedLoggedOut && (
                        <InlineMessage
                            prefix={stylePrefix}
                            title={"You've been signed out."}
                            body={"Sign in to save your progress across your devices."}
                            variant={"negative"} 
                            className={"signed-out-warning"}  
                        />
                    )}
                    {/* Todo- consider using forwardRef instead of a wrapper div */}
                    <div ref={pdfRef}>
                        <Checklist />
                    </div>
                    <Button
                        small={isDesktop}
                        prefix={stylePrefix}
                        text={"Download printable version"}
                        iconPosition={"trailing"}
                        ssrIcon={Printer}
                        loading={pdfLoading}
                        type={"secondary"}
                        onClick={handleDownload}
                    />
                </Fragment>
            ) : (
                <Loading text="Loading..." labelTransitions>
                    <LoadingBall color="emphasised" />
                </Loading>
            )}
        </div>
    );
}

export default ChecklistPage;
